import {
  ACCESS_ROLE_ADMIN,
  ACCESS_ROLE_EMPLOYEE,
  ACCESS_ROLE_MANAGER,
  ACCESS_ROLE_NEW_HIRE,
  ACCESS_ROLE_OPERATIONS_SUPPORT,
  ACCESS_ROLE_OWNER_MANAGER,
  ACCESS_ROLE_SHIFT_LEAD,
  WORK_STATUS_ACTIVE,
  WORK_STATUS_ARCHIVED,
  WORK_STATUS_PAUSED,
  WORK_STATUS_PENDING_ONBOARDING,
  WORK_STATUS_SUSPENDED,
  WORK_STATUS_TERMINATED
} from '../workforceAccessModel'

export const ADMIN_ACCESS_ROLES = [
  ACCESS_ROLE_NEW_HIRE,
  ACCESS_ROLE_EMPLOYEE,
  ACCESS_ROLE_SHIFT_LEAD,
  ACCESS_ROLE_MANAGER,
  ACCESS_ROLE_OWNER_MANAGER,
  ACCESS_ROLE_OPERATIONS_SUPPORT,
  ACCESS_ROLE_ADMIN
]

export const ADMIN_WORK_STATUSES = [
  WORK_STATUS_PENDING_ONBOARDING,
  WORK_STATUS_ACTIVE,
  WORK_STATUS_PAUSED,
  WORK_STATUS_SUSPENDED,
  WORK_STATUS_TERMINATED,
  WORK_STATUS_ARCHIVED
]

export const ADMIN_SECTION_KEYS = {
  DASHBOARD: 'dashboard',
  ACCESS: 'access',
  WORKFORCE: 'workforce',
  CONTACTS: 'contacts',
  TIME_PAY: 'time_pay',
  ISSUES: 'issues',
  CORRECTIONS: 'corrections',
  AUDIT: 'audit',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  ANNOUNCEMENTS: 'announcements'
}

export const ADMIN_ISSUE_CATEGORIES = [
  { value: 'attendance_issue', label: 'Attendance Issue' },
  { value: 'site_issue', label: 'Site Issue' },
  { value: 'pay_dispute', label: 'Pay Dispute' },
  { value: 'worker_conduct_issue', label: 'Worker Conduct Issue' },
  { value: 'safety_incident', label: 'Safety Incident' },
  { value: 'equipment_material_issue', label: 'Equipment/Material Issue' },
  { value: 'client_vendor_complaint', label: 'Client/Vendor Complaint' }
]

export const ADMIN_ISSUE_STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' }
]

export const PAY_STATUS_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'disputed', label: 'Disputed' }
]

export const PAY_SOURCE_STATUS_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'vendor_unpaid', label: 'Vendor Unpaid' },
  { value: 'vendor_paid', label: 'Vendor Paid' }
]

export const ADMIN_REASON_CODES = [
  { value: 'admin_correction', label: 'Admin Correction' },
  { value: 'role_change', label: 'Role Change' },
  { value: 'status_change', label: 'Status Change' },
  { value: 'manual_time_adjustment', label: 'Manual Time Adjustment' },
  { value: 'pay_visibility_update', label: 'Pay Visibility Update' },
  { value: 'issue_resolution', label: 'Issue Resolution' },
  { value: 'record_archive', label: 'Record Archive' },
  { value: 'record_deactivate', label: 'Record Deactivate' },
  { value: 'binding_repair', label: 'Binding Repair' }
]

const FULL_SECTIONS = new Set(Object.values(ADMIN_SECTION_KEYS))

const SECTION_PERMISSIONS_BY_ACCESS_ROLE = {
  [ACCESS_ROLE_ADMIN]: FULL_SECTIONS,
  [ACCESS_ROLE_OWNER_MANAGER]: FULL_SECTIONS,
  [ACCESS_ROLE_MANAGER]: new Set(Object.values(ADMIN_SECTION_KEYS).filter((key) => key !== ADMIN_SECTION_KEYS.ACCESS)),
  [ACCESS_ROLE_OPERATIONS_SUPPORT]: new Set(
    Object.values(ADMIN_SECTION_KEYS).filter((key) => ![ADMIN_SECTION_KEYS.ACCESS, ADMIN_SECTION_KEYS.SETTINGS].includes(key))
  ),
  [ACCESS_ROLE_SHIFT_LEAD]: new Set([
    ADMIN_SECTION_KEYS.DASHBOARD,
    ADMIN_SECTION_KEYS.TIME_PAY,
    ADMIN_SECTION_KEYS.ISSUES,
    ADMIN_SECTION_KEYS.CORRECTIONS,
    ADMIN_SECTION_KEYS.REPORTS,
    ADMIN_SECTION_KEYS.ANNOUNCEMENTS
  ]),
  [ACCESS_ROLE_EMPLOYEE]: new Set(),
  [ACCESS_ROLE_NEW_HIRE]: new Set()
}

export const normalizeAccessRoleValue = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
  if (ADMIN_ACCESS_ROLES.includes(normalized)) return normalized
  return ACCESS_ROLE_EMPLOYEE
}

export const canAccessAdminOps = (accessRole = '') => {
  const normalized = normalizeAccessRoleValue(accessRole)
  const sections = SECTION_PERMISSIONS_BY_ACCESS_ROLE[normalized]
  return Boolean(sections && sections.size > 0)
}

export const getAdminSectionPermissions = (accessRole = '') => {
  const normalized = normalizeAccessRoleValue(accessRole)
  const sections = SECTION_PERMISSIONS_BY_ACCESS_ROLE[normalized] || new Set()
  return {
    accessRole: normalized,
    canOpenAdminOps: sections.size > 0,
    sections: new Set(sections)
  }
}

export const canAccessAdminSection = (accessRole = '', section = '') => {
  if (!section) return false
  return getAdminSectionPermissions(accessRole).sections.has(section)
}

export const getDefaultAdminSectionForRole = (accessRole = '') => {
  const permissions = getAdminSectionPermissions(accessRole)
  if (permissions.sections.has(ADMIN_SECTION_KEYS.DASHBOARD)) return ADMIN_SECTION_KEYS.DASHBOARD
  if (permissions.sections.has(ADMIN_SECTION_KEYS.TIME_PAY)) return ADMIN_SECTION_KEYS.TIME_PAY
  if (permissions.sections.size > 0) return [...permissions.sections][0]
  return ADMIN_SECTION_KEYS.DASHBOARD
}

export const formatAccessRoleLabel = (accessRole = '') => {
  const normalized = normalizeAccessRoleValue(accessRole)
  if (normalized === ACCESS_ROLE_NEW_HIRE) return 'New Hire'
  if (normalized === ACCESS_ROLE_SHIFT_LEAD) return 'Shift Lead'
  if (normalized === ACCESS_ROLE_OWNER_MANAGER) return 'Owner Manager'
  if (normalized === ACCESS_ROLE_OPERATIONS_SUPPORT) return 'Operations Support'
  return normalized.charAt(0).toUpperCase() + normalized.slice(1)
}

export const formatWorkStatusLabel = (workStatus = '') => {
  const normalized = String(workStatus || '').trim().toLowerCase()
  if (normalized === WORK_STATUS_PENDING_ONBOARDING) return 'Pending Onboarding'
  if (normalized === WORK_STATUS_SUSPENDED) return 'Suspended'
  if (normalized === WORK_STATUS_TERMINATED) return 'Terminated'
  if (normalized === WORK_STATUS_ARCHIVED) return 'Archived'
  if (normalized === WORK_STATUS_PAUSED) return 'Paused'
  if (normalized === WORK_STATUS_ACTIVE) return 'Active'
  return 'Unknown'
}

export const ADMIN_PERMISSION_MATRIX = ADMIN_ACCESS_ROLES.map((accessRole) => {
  const sections = getAdminSectionPermissions(accessRole).sections
  const matrixRow = {
    accessRole,
    accessRoleLabel: formatAccessRoleLabel(accessRole)
  }

  Object.values(ADMIN_SECTION_KEYS).forEach((section) => {
    matrixRow[section] = sections.has(section)
  })

  return matrixRow
})
